/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useContext} from 'react';
import {useMutation} from '@apollo/client';
import StickyHeadTable, {
  ColumnProps,
} from 'components/StickyHeadTable/StickyHeadTable';
import ModalController from 'controllers/ModalController';
import {Button, IconButton} from '@mui/material';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {ConfigurationTableProps} from 'models/configuration';
import AddParamModal from 'viewComponents/AddParamModal/AddParamModal';
import DeleteParamModal from 'viewComponents/DeleteParamModal/DeleteParamModal';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';
import {GET_MONETIZATION_CONFIGURATIONS} from 'graphql/absences/Querys';
import {
  CREATE_MONETIZATION_CONFIGURATION,
  UPDATE_MONETIZATION_CONFIGURATION,
} from 'graphql/admin/Mutation';
import {useTranslation} from 'react-i18next';
import {alertContext} from 'contextApi/context/error';

const MonetizationParamsTable: React.FC<ConfigurationTableProps> = ({
  country,
  fetch,
  isActive,
}) => {
  const {t} = useTranslation();
  const {dispatch} = useContext(alertContext);
  const [updateMonetization] = useMutation(UPDATE_MONETIZATION_CONFIGURATION);

  const getErrorMessage = (error: any) => {
    return (
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message ||
      t('common.generic_error', 'Algo salió mal')
    );
  };

  const renderDisableController = (
    values: any,
    refetchCallback: () => void
  ) => (
    <IconButton
      onClick={async () => {
        const nextStatus = values.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        await updateMonetization({
          variables: {
            id: values.id,
            days: values.days,
            status: nextStatus,
          },
        });
        refetchCallback();
        await fetch();
      }}
    >
      {values.status === 'ACTIVE' ? (
        <ToggleOnIcon sx={{color: 'success.main'}} />
      ) : (
        <ToggleOffIcon />
      )}
    </IconButton>
  );

  const renderEditController = (values: any, refetchCallback: () => void) => (
    <ModalController
      trigger={({onToggleModal}) => (
        <IconButton onClick={onToggleModal}>
          <EditIcon />
        </IconButton>
      )}
      modal={({isModalOpen, onToggleModal}) => (
        <AddParamModal
          title={t(
            'leaves.MonetizationParamsTable.editModalTitle',
            'Modificar monetización'
          )}
          submitButtonText={t(
            'leaves.MonetizationParamsTable.editSubmitButton',
            'Guardar cambios'
          )}
          cancelButtonText={t('leaves.AddParamModal.cancel')}
          formProps={{
            controls: [
              {
                name: 'days',
                options: {
                  required: true,
                },
                props: {
                  label: t('leaves.MonetizationParamsTable.days', 'Días'),
                  inputProps: {type: 'number', min: 1, step: 'any'},
                },
              },
            ],
            defaultValues: {
              days: values.days,
            },
            variablesMapper: (formValues: any) => {
              const days = parseFloat(formValues.days);
              if (isNaN(days) || days <= 0) {
                throw new Error('Los días deben ser mayor a 0');
              }
              return {
                id: values.id,
                days,
                status: values.status,
              };
            },
            onSubmit: async (mutation: any) => {
              await mutation;
              refetchCallback();
              await fetch();
              onToggleModal();
            },
            mutation: UPDATE_MONETIZATION_CONFIGURATION,
          }}
          onClose={onToggleModal}
          open={isModalOpen}
        />
      )}
    />
  );

  const renderDeleteController = (values: any, refetchCallback: () => void) => (
    <ModalController
      trigger={({onToggleModal}) => (
        <IconButton onClick={onToggleModal}>
          <DeleteIcon />
        </IconButton>
      )}
      modal={({isModalOpen, onToggleModal}) => (
        <DeleteParamModal
          variablesMapper={() => {
            return {
              id: values.id,
              days: values.days,
              status: 'DELETED',
            };
          }}
          onSubmit={() => {
            refetchCallback();
            fetch();
          }}
          mutation={UPDATE_MONETIZATION_CONFIGURATION}
          values={values}
          onClose={onToggleModal}
          open={isModalOpen}
        />
      )}
    />
  );

  return (
    <div className="MonetizationParamsTable w-full flex flex-col flex-wrap">
      <GqlQueryController
        QUERY={GET_MONETIZATION_CONFIGURATIONS}
        variables={{
          country,
        }}
        paramValidator={(_: any, current: any) => !!current?.country}
        initialState={{data: []} as any}
        rootField="getMonetizationConfigurations"
      >
        {({
          data: monetizationResponse,
          isLoading,
          fetch: refetchMonetization,
        }) => {
          const monetizationRows = monetizationResponse?.data || [];
          const hasActiveMonetization = monetizationRows.some(
            (item: any) => item.status === 'ACTIVE'
          );

          const tableRows = monetizationRows.filter((item: any) =>
            ['ACTIVE', 'INACTIVE'].includes(item.status)
          );

          const columns: ColumnProps[] = [
            {
              id: 'leaveType',
              label: t('leaves.MonetizationParamsTable.type', 'Tipo'),
              render: (leaveType: any) =>
                leaveType?.description || leaveType?.name,
            },
            {
              id: 'days',
              label: t('leaves.MonetizationParamsTable.days', 'Días'),
            },
            {
              id: 'status',
              label: t('leaves.MonetizationParamsTable.status', 'Estatus'),
            },
            {
              id: 'id',
              label: t('leaves.MonetizationParamsTable.actions', 'Acciones'),
              align: 'right',
              render: (_, values: any) => (
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end',
                  }}
                >
                  {renderDisableController(values, refetchMonetization)}
                  {renderEditController(values, refetchMonetization)}
                  {renderDeleteController(values, refetchMonetization)}
                </div>
              ),
            },
          ];

          return (
            <>
              <div className="flex flex-row justify-between items-center py-6">
                <p className="m-5 text-sm">
                  Sólo puede existir un tipo de monetización activo
                </p>
                <ModalController
                  trigger={({onToggleModal}) => (
                    <Button
                      variant="outlined"
                      disabled={hasActiveMonetization || isActive === false}
                      onClick={() => {
                        onToggleModal();
                      }}
                    >
                      {t(
                        'leaves.MonetizationParamsTable.createButtonLabel',
                        'Agregar monetización'
                      )}
                    </Button>
                  )}
                  modal={({isModalOpen, onToggleModal}) => (
                    <AddParamModal
                      title={t(
                        'leaves.MonetizationParamsTable.createModalTitle',
                        'Crear monetización'
                      )}
                      submitButtonText={t(
                        'leaves.MonetizationParamsTable.createSubmitButton',
                        'Agregar'
                      )}
                      cancelButtonText={t('leaves.AddParamModal.cancel')}
                      formProps={{
                        controls: [
                          {
                            name: 'days',
                            options: {
                              required: true,
                            },
                            props: {
                              label: t(
                                'leaves.MonetizationParamsTable.days',
                                'Días'
                              ),
                              inputProps: {type: 'number', min: 1, step: 'any'},
                            },
                          },
                        ],
                        variablesMapper: (values: any) => {
                          const days = parseFloat(values.days);
                          if (isNaN(days) || days <= 0) {
                            throw new Error('Los días deben ser mayor a 0');
                          }
                          return {
                            country,
                            days,
                          };
                        },
                        onSubmit: async (mutation: any) => {
                          try {
                            await mutation;
                            await refetchMonetization();
                            await fetch();
                            onToggleModal();
                          } catch (error: any) {
                            dispatch({
                              type: 'show',
                              payload: {
                                level: 'error',
                                message: getErrorMessage(error),
                              },
                            });
                          }
                        },
                        mutation: CREATE_MONETIZATION_CONFIGURATION,
                      }}
                      onClose={onToggleModal}
                      open={isModalOpen}
                    />
                  )}
                />
              </div>

              <StickyHeadTable
                columns={columns}
                placeholder={t('common.empty_list')}
                hidePagination
                isLoading={isLoading}
                rows={tableRows}
                onPageChange={() => {}}
              />
            </>
          );
        }}
      </GqlQueryController>
    </div>
  );
};

export default MonetizationParamsTable;
